import type { ReactNode } from "react";
import { PersonalChannelHeader } from "./components/PersonalChannelHeader";
import { SharedByBanner } from "./components/SharedByBanner";
import { VideoPlayerCard } from "./components/VideoPlayerCard";
import { ClipListSidebar } from "./components/ClipListSidebar";
import { DeleteChannelDialog } from "@/v2/components/channel/DeleteChannelDialog";
import { ShareChannelDialog } from "@/v2/components/channel/ShareChannelDialog";
import { ChannelSharesDialog } from "./components/ChannelSharesDialog";
import type { ChannelSummary } from "./components/ChannelSwitcherPanel";
import type { ClipListItem } from "./components/ClipRow";
import type { DownloadRecord } from "./download";

export function PersonalChannelPage({
  channelName,
  isShared,
  sharedByEmail,
  allChannels,
  currentChannelId,
  onSelectChannel,
  incomingRequestsCount,
  onOpenSharesDialog,
  onAddChannel,
  onEditChannel,
  onDeleteChannel,
  onShareChannel,

  videoList,
  page,
  pageData,
  isSkeletonList,
  activeClip,
  onSelectClip,
  handlePrevious,
  handleNext,
  currentVideoData,
  videoPlayer,

  allDownloadVideo,
  isDownloadAll,
  setIsDownloadAll,
  subscriptionDetails,
  userDetails,
  onDownloadRequested,

  deleteDialogOpen,
  onCloseDeleteDialog,
  onConfirmDelete,

  shareDialogOpen,
  onCloseShareDialog,
  channelIdForShare,
  targetChannelName,

  sharesDialogOpen,
  onCloseSharesDialog,
  incomingRequests,
  shareActionLoading,
  onAcceptShare,
  onDeclineShare,
  onRefreshIncomingShares,
  onRefreshChannelShares,
  channelShares,
  cancellingShareId,
  onCancelShare,
}: {
  channelName: string;
  isShared: boolean;
  sharedByEmail?: string | null;
  allChannels: ChannelSummary[];
  currentChannelId: string | number;
  onSelectChannel: (id: string | number) => void;
  incomingRequestsCount: number;
  onOpenSharesDialog: () => void;
  onAddChannel: () => void;
  onEditChannel: (channelId?: string | number) => void;
  onDeleteChannel: (channelId?: string | number) => void;
  onShareChannel: (channelId?: string | number) => void;

  videoList: ClipListItem[];
  page: number;
  pageData: any;
  isSkeletonList: boolean;
  activeClip: string | number;
  onSelectClip: (clip: ClipListItem, index: number) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  currentVideoData: ClipListItem | undefined;
  videoPlayer: ReactNode;

  allDownloadVideo: DownloadRecord[];
  isDownloadAll: boolean;
  setIsDownloadAll: (value: boolean) => void;
  subscriptionDetails: any;
  userDetails: any;
  /** Refetches the download records after a clip download is requested, so the
   *  row's disabled state comes from record.Status and survives paging. */
  onDownloadRequested?: () => void;

  deleteDialogOpen: boolean;
  onCloseDeleteDialog: () => void;
  onConfirmDelete: () => void;

  shareDialogOpen: boolean;
  onCloseShareDialog: () => void;
  channelIdForShare: string | number;
  /** Name of the channel the dialogs are acting on, which is not always the
   *  one on screen: the switcher rows can share or delete any channel. */
  targetChannelName: string;

  sharesDialogOpen: boolean;
  onCloseSharesDialog: () => void;
  incomingRequests: any[];
  shareActionLoading: string | null;
  onAcceptShare: (token: string) => void;
  onDeclineShare: (token: string) => void;
  onRefreshIncomingShares: () => void;
  onRefreshChannelShares: () => void;
  channelShares: any[];
  cancellingShareId: number | null;
  onCancelShare: (shareId: number) => void;
}) {
  /**
   * The player runs one concatenated stream of every clip in the list and
   * reports which clip it has reached through setActiveClip (its TimeChanged
   * handler). currentVideoData, by contrast, is only ever set to list[0] when
   * the page loads, so the panel under the player kept naming the first clip
   * no matter what was on screen. Resolve the clip from activeClip instead,
   * falling back to currentVideoData before the first TimeChanged fires.
   */
  const playingClip =
    videoList?.find(
      (clip) => String(clip.game_event_id) === String(activeClip),
    ) ?? currentVideoData;

  return (
    <div className="flex flex-col gap-3">
      {/* Plain media query instead of an lg:w-[360px] Tailwind arbitrary
          value - that class silently failed to generate once in dev (no
          matching rule ever appeared in any loaded stylesheet), so the
          sidebar width is pinned here where it can't be dropped by a JIT
          scan miss. */}
      <style>{`
        .personal-channel-sidebar { width: 100%; }
        @media (min-width: 1024px) {
          .personal-channel-sidebar { width: 360px; flex-shrink: 0; position: relative; }
          /* The rail should end level with the player card, never past it.
             Left in flow its clip list would grow to fit 10+ rows and become
             the tallest item, so it sets the row height instead of following
             it. Taking the card out of flow means the player column alone
             decides how tall the row is, and the card then fills exactly that. */
          .personal-channel-sidebar > * { position: absolute; inset: 0; }
        }
      `}</style>
      <PersonalChannelHeader
        channelName={channelName}
        allChannels={allChannels}
        currentChannelId={currentChannelId}
        onSelectChannel={onSelectChannel}
        incomingRequestsCount={incomingRequestsCount}
        onOpenShares={onOpenSharesDialog}
        onAddChannel={onAddChannel}
        onEditChannel={onEditChannel}
        onDeleteChannel={onDeleteChannel}
        onShareChannel={onShareChannel}
        showShareAction={!isShared}
      />

      {isShared && <SharedByBanner sharedByEmail={sharedByEmail} />}

      {videoList?.length > 0 || page !== 1 ? (
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 min-w-0">
            {/* Downloading lives on the clip rows in the list, so the player
                card only needs to name what is playing. */}
            <VideoPlayerCard currentVideoData={playingClip}>
              {videoPlayer}
            </VideoPlayerCard>
          </div>

          <div className="personal-channel-sidebar">
            <ClipListSidebar
              videoList={videoList}
              pageData={pageData}
              isSkeletonList={isSkeletonList}
              activeClip={activeClip}
              onSelectClip={onSelectClip}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              allDownloadVideo={allDownloadVideo}
              isDownloadAll={isDownloadAll}
              setIsDownloadAll={setIsDownloadAll}
              subscriptionDetails={subscriptionDetails}
              userDetails={userDetails}
              onDownloadRequested={onDownloadRequested}
            />
          </div>
        </div>
      ) : (
        <div className="text-center text-slate-500" style={{ padding: "64px 0" }}>
          No data found please update your preference
        </div>
      )}

      <DeleteChannelDialog
        open={deleteDialogOpen}
        onClose={onCloseDeleteDialog}
        onConfirm={onConfirmDelete}
        channelName={targetChannelName}
      />
      <ShareChannelDialog
        open={shareDialogOpen}
        onClose={onCloseShareDialog}
        channelId={channelIdForShare}
        channelName={targetChannelName}
      />
      <ChannelSharesDialog
        open={sharesDialogOpen}
        onClose={onCloseSharesDialog}
        incomingRequests={incomingRequests}
        shareActionLoading={shareActionLoading}
        onAccept={onAcceptShare}
        onDecline={onDeclineShare}
        onRefreshIncoming={onRefreshIncomingShares}
        onRefreshShares={onRefreshChannelShares}
        channelShares={channelShares}
        cancellingShareId={cancellingShareId}
        onCancelShare={onCancelShare}
      />
    </div>
  );
}
